import {
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  BlockUI,
  NgBlockUI
} from 'ng-block-ui';

import {
  Observable,
  Subscription
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { HaynesProLaborOptionModel }     from './typings/haynes-pro.labor-option';
import { HaynesProLaborTimeModel }       from './typings/haynes-pro.labor-time';
import { HaynesProService }              from './haynes-pro.service';
import { HaynesProServiceIntervalModel } from './typings/haynes-pro.service-interval';
import { Table }                         from '../table/types/table';
import { VehicleModel }                  from '../vehicles/models/vehicle';
import { PushPopComponent }              from '../table/cell-components/actions/push-pop.component';

@Component({
  selector   : 'wor-haynes-pro-maintenance-times',
  template: require('./haynes-pro.maintenance-times.component.html')
})
export class HaynesProMaintenanceTimesComponent implements OnDestroy, OnInit {
  private laborTimeRemovalSubscription: Subscription;
  public tableSettings                : Table;

  public laborOptions      : Array<HaynesProLaborOptionModel> = [];
  public laborTimes        : Array<HaynesProLaborTimeModel>   = [];

  @BlockUI('haynesProMaintenanceTimesBlock') block : NgBlockUI;

  @Input() intervals       : Array<HaynesProServiceIntervalModel>;
  @Input() removedLaborTime: Observable<HaynesProLaborTimeModel>;
  @Input() vehicle         : VehicleModel;
  @Input() viewMode        : boolean;

  @Output() onTimeAdd      : EventEmitter<HaynesProLaborTimeModel> = new EventEmitter();
  @Output() onTimeRemove   : EventEmitter<HaynesProLaborTimeModel> = new EventEmitter();

  constructor (
    @Inject(forwardRef(() => HaynesProService))
    private haynesProService: HaynesProService,
    public translateService : TranslateService
  ) {}

  ngOnDestroy () : void {
    if (this.laborTimeRemovalSubscription) {
      this.laborTimeRemovalSubscription.unsubscribe();
    }
  }

  ngOnInit () : void {
    this.laborTimeRemovalSubscription = this.removedLaborTime.subscribe(( time : HaynesProLaborTimeModel ) => {
      if (time.builtFromServiceInterval) {
        this.haynesProService.updateServiceIntervalsWithRemovedLaborTime(this.intervals, time);
      }
      else {
        this.haynesProService.removeLaborTime(this.laborTimes, time);
      }
    });

    this.tableSettings = {
      columns: this.buildColumns(),
      empty  : {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_RECORDS_FOUND', {
        type: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.MAINTENANCE_TIMES')
      })
      },
      pagination: {
        enabled: false
      },
      sorting: {
        enabled: false
      },
      static: true
    };
  }

  buildColumns() {
    const columns:any = [
      {
        field : 'period_name',
        header: {
          text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.PERIOD_NAME')
        }
      },
      {
        field : 'labour_time',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.LABOUR_TIME')
        }
      }
    ];
    if (!this.viewMode) {
      columns.push({
        classes  : [ 'text-right' ],
        component: {
          bindings: {
            inputs: {
              popTitle : this.translateService.instant('GENERAL_SPACE.BUTTON.REMOVE_FROM_CART'),
              pushTitle: this.translateService.instant('GENERAL_SPACE.BUTTON.ADD_TO_CART'),
            },
            outputs: {
              onPop : ( interval : HaynesProServiceIntervalModel ) => this.handleIntervalRemoval(interval),
              onPush: ( interval : HaynesProServiceIntervalModel ) => this.handleIntervalSelection(interval)
            }
          },
          enabled: true,
          factory: PushPopComponent,
        },
        sortable: false,
      });
    }

    return columns;
  };


  handleIntervalSelection ( interval : HaynesProServiceIntervalModel ) : void {
    this.block.start();

    this.haynesProService.getLaborOptions(this.vehicle, interval)
    .then(( laborOptions : Array<HaynesProLaborOptionModel> ) => {
      this.laborTimes   = [];

      this.onTimeAdd.emit(this.haynesProService.buildLaborTimeFromInterval(interval, laborOptions));

      this.laborOptions = laborOptions;
    })
    .finally(() => {
      this.block.stop();
    });
  }

  handleIntervalRemoval ( interval : HaynesProServiceIntervalModel ) : void {
    this.onTimeRemove.emit(this.haynesProService.buildLaborTimeFromInterval(interval, this.laborOptions));

    this.laborOptions = [];
    this.laborTimes   = [];
  }

  handleLaborOptionSelection ( option : HaynesProLaborOptionModel ) : void {
    this.block.start();

    this.laborTimes = [];

    setTimeout(() => {
      this.laborTimes = option.tasks;

      this.block.stop();
    }, 500);
  }
}
